import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ShoppingBag, Menu, X, User } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";

export const LogoMark = ({ className = "h-9 w-9" }) => (
  <svg viewBox="0 0 64 64" className={className} aria-hidden="true">
    <rect width="64" height="64" rx="16" fill="#0D3220" />
    <path d="M32 50c0-11 6.5-19.5 17-21.5C47.6 40 41 48 32 50Z" fill="#12B76A" />
    <path d="M32 50c0-11-6.5-19.5-17-21.5C16.4 40 23 48 32 50Z" fill="#FAF7F2" />
    <circle cx="32" cy="15" r="3.5" fill="#12B76A" />
  </svg>
);

const links = [
  { to: "/shop", label: "Shop" },
  { to: "/distribute", label: "Distribute" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const { count, openCart } = useCart();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  return (
    <header className="fixed inset-x-0 top-0 z-50 border-b border-sand/80 bg-cream/80 backdrop-blur-md">
      <nav className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6">
        <Link to="/" data-testid="nav-home-link" className="flex items-center gap-2.5">
          <LogoMark />
          <span className="font-display text-xl font-extrabold lowercase tracking-tight text-forest">
            abhivesha
            <span className="ml-1.5 align-middle font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-leaf">foods</span>
          </span>
        </Link>

        <div className="hidden items-center gap-1 md:flex">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className="rounded-full px-4 py-2 text-sm font-bold text-ink/80 transition-colors hover:bg-forest/5 hover:text-forest"
            >
              {l.label}
            </Link>
          ))}
        </div>

        <div className="flex items-center gap-2">
          <button
            data-testid="nav-account-button"
            onClick={() => navigate("/account")}
            aria-label="Account"
            className="flex h-11 items-center gap-2 rounded-full border border-sand bg-white px-3.5 text-sm font-bold text-forest transition-colors hover:border-leaf hover:text-leaf"
          >
            {user ? (
              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-leaf font-display text-[11px] font-extrabold text-white">
                {(user.name || user.email)[0].toUpperCase()}
              </span>
            ) : (
              <User className="h-4 w-4" />
            )}
            <span className="hidden max-w-24 truncate sm:inline">{user ? user.name?.split(" ")[0] || "Account" : "Account"}</span>
          </button>
          <button
            data-testid="nav-cart-button"
            onClick={openCart}
            aria-label="Open cart"
            className="relative flex h-11 items-center gap-2 rounded-full bg-forest px-4 text-sm font-bold text-cream transition-all duration-200 hover:bg-leaf active:scale-95"
          >
            <ShoppingBag className="h-4 w-4" />
            <span className="hidden sm:inline">Cart</span>
            {count > 0 && (
              <span data-testid="nav-cart-count" className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-leaf px-1 font-mono text-[11px] font-bold text-white">
                {count}
              </span>
            )}
          </button>
          <button
            data-testid="nav-menu-toggle"
            onClick={() => setOpen(!open)}
            aria-label="Toggle menu"
            className="flex h-11 w-11 items-center justify-center rounded-full border border-sand bg-white text-forest md:hidden"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2 }}
            className="border-t border-sand bg-cream px-4 py-3 md:hidden"
          >
            {[...links, { to: "/account", label: user ? `Account (${user.name?.split(" ")[0]})` : "Account" }].map((l) => (
              <button
                key={l.to}
                data-testid={`nav-mobile-link-${l.label.toLowerCase().split(" ")[0].replace(/[^a-z]/g, "")}`}
                onClick={() => { setOpen(false); navigate(l.to); }}
                className="block w-full rounded-xl px-4 py-3 text-left font-display text-lg font-bold text-forest hover:bg-forest/5"
              >
                {l.label}
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
